import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { TORCH_GENESIS_KIND } from '@/lib/torchConstants';
import { Flame, ArrowLeft, Loader2, Zap, Clock, Turtle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TTLPreset {
  label: string;
  sublabel: string;
  desc: string;
  hours: number;
  icon: React.ReactNode;
}

const TTL_PRESETS: TTLPreset[] = [
  { label: 'Quick', sublabel: '24 hours', desc: 'Fast-moving torch — pass it or it dies tomorrow.', hours: 24, icon: <Zap className="w-5 h-5" /> },
  { label: 'Standard', sublabel: '1 week', desc: 'A week to find the next holder.', hours: 168, icon: <Flame className="w-5 h-5" /> },
  { label: 'Slow Burn', sublabel: '30 days', desc: 'Plenty of time — but the clock never stops.', hours: 720, icon: <Turtle className="w-5 h-5" /> },
];

export function CreateTorch() {
  const { user } = useCurrentUser();
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [ttlHours, setTtlHours] = useState(168);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slug || slug === title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
      setSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40));
    }
  };

  const handleSubmit = () => {
    if (!user) return;

    const seed = crypto.getRandomValues(new Uint8Array(32));
    const seedHex = Array.from(seed).map(b => b.toString(16).padStart(2, '0')).join('');

    publishEvent({
      kind: TORCH_GENESIS_KIND,
      tags: [
        ['d', slug],
        ['title', title],
        ['description', description],
        ['seed', seedHex],
        ['ttl', String(ttlHours)],
        ['alt', `Torchlite torch: ${title}`],
      ],
      content: '',
    }, {
      onSuccess: () => {
        navigate(`/torch/${slug}`);
      },
    });
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Login Required</h1>
          <p className="text-muted-foreground mb-4">You need to login with a Nostr identity to create a torch.</p>
          <Link to="/"><Button>← Back</Button></Link>
        </div>
      </Layout>
    );
  }

  const selectedPreset = TTL_PRESETS.find(p => p.hours === ttlHours);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
            <Flame className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold">Light a Torch</h1>
          <p className="text-muted-foreground">Start a new flame and pass it to the world.</p>
        </div>

        <div className="space-y-6">
          {/* Identity */}
          <Card>
            <CardHeader><CardTitle>Identity</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Name</Label>
                <Input
                  id="title"
                  placeholder="The Olympic Flame"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="slug">
                  URL slug
                  <span className="text-muted-foreground font-normal ml-2 text-xs">torchlite.shakespeare.wtf/torch/{slug || '…'}</span>
                </Label>
                <Input
                  id="slug"
                  placeholder="olympic-flame"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40))}
                  className="mt-2 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="description">Description <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                <Textarea
                  id="description"
                  placeholder="Tell the story of this torch…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-2"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* TTL */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Time to Pass
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                How long each holder has to zap the next person before the torch is extinguished.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {TTL_PRESETS.map((preset) => (
                  <button
                    key={preset.hours}
                    type="button"
                    onClick={() => setTtlHours(preset.hours)}
                    className={cn(
                      'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all text-center',
                      ttlHours === preset.hours
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300'
                        : 'border-border hover:border-orange-300 text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <span className={ttlHours === preset.hours ? 'text-orange-500' : ''}>{preset.icon}</span>
                    <span className="font-semibold text-sm">{preset.label}</span>
                    <span className="text-xs">{preset.sublabel}</span>
                  </button>
                ))}
              </div>
              {selectedPreset && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  {selectedPreset.desc}
                </p>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={handleSubmit}
            disabled={!title || !slug || isPending}
            className="w-full bg-orange-500 hover:bg-orange-600 h-12 text-base"
          >
            {isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
            ) : (
              <><Flame className="w-4 h-4 mr-2" />Light Torch</>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
